// Hooks de «Cobros»: mi cartera (loan-credit), el detalle del crédito abierto con sus cobros, el
// walk-in y los cobros del walk-in.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '@/api/errors';
import { listSellerCreditPayments, type LoanPaymentListItem } from '@/api/services/loan-credit';
import { creditReadStateOf } from '../../customers/api/creditPortfolioLoader';
import type { CollectionCredit, WalkInKind } from '../lib/collectionsModel';
import {
    DEFAULT_COLLECTIONS_DEPS,
    loadSellerCredit,
    loadWalkIn,
    sellerCreditRowOf,
    type SellerCreditLoad,
    type WalkInResult,
} from './collectionsLoaders';

export const COLLECTIONS_KEYS = {
    all: ['loan-credit', 'collections'] as const,
    mine: ['loan-credit', 'collections', 'mine'] as const,
    sellerCredit: (creditId: string | null) =>
        ['loan-credit', 'collections', 'seller-credit', creditId] as const,
    walkIn: (kind: WalkInKind | null, term: string, mine: string) =>
        ['loan-credit', 'collections', 'walk-in', kind, term, mine] as const,
    payments: (creditId: string | null) =>
        ['loan-credit', 'collections', 'payments', creditId] as const,
};

/** El 409 de `GET /seller/credits` cuando el usuario no tiene alta en retail-org. */
const SELLER_NOT_REGISTERED = 'SELLER_NOT_REGISTERED';

export interface MyCreditsResult {
    rows: CollectionCredit[];
    isLoading: boolean;
    /** Falla reintentable; «sin permiso» y «sin alta de vendedor» van aparte. */
    error: Error | null;
    forbidden: boolean;
    /** El usuario no tiene alta de vendedor (409): reintentar no lo arregla. */
    notRegistered: boolean;
    /** El servidor se detuvo con cursor pendiente: la cartera mostrada está incompleta. */
    truncated: boolean;
    refetch: () => void;
}

/** Mis créditos: UNA llamada a loan-credit con el resumen de cada uno. */
export function useMyCredits(): MyCreditsResult {
    const query = useQuery({
        queryKey: COLLECTIONS_KEYS.mine,
        queryFn: () => DEFAULT_COLLECTIONS_DEPS.listSellerCredits(),
        staleTime: 60_000,
        retry: false,
    });
    const rows = useMemo(() => (query.data?.items ?? []).map(sellerCreditRowOf), [query.data]);
    const forbidden = query.error ? creditReadStateOf(query.error) === 'forbidden' : false;
    const notRegistered =
        query.error instanceof ApiError && query.error.code === SELLER_NOT_REGISTERED;
    return {
        rows,
        isLoading: query.isPending,
        error: forbidden || notRegistered ? null : query.error,
        forbidden,
        notRegistered,
        truncated: query.data?.truncated ?? false,
        refetch: () => void query.refetch(),
    };
}

/** Detalle del crédito propio abierto (crédito, plan y cobros); no corre sin crédito. */
export function useSellerCredit(creditId: string | null): {
    load: SellerCreditLoad;
    refetch: () => void;
} {
    const query = useQuery({
        queryKey: COLLECTIONS_KEYS.sellerCredit(creditId),
        queryFn: () => loadSellerCredit(creditId ?? '', DEFAULT_COLLECTIONS_DEPS),
        enabled: creditId !== null,
        staleTime: 60_000,
    });
    return {
        load: query.data ?? { read: { state: 'loading' }, recentPayments: [] },
        refetch: () => void query.refetch(),
    };
}

/** Walk-in: solo corre con un identificador completo y sin coincidencias en mi cartera. */
export function useWalkIn(
    kind: WalkInKind | null,
    term: string,
    mineIds: Set<string>,
): { result: WalkInResult | null; isLoading: boolean } {
    const mineSignature = [...mineIds].sort().join(',');
    const query = useQuery({
        queryKey: COLLECTIONS_KEYS.walkIn(kind, term, mineSignature),
        queryFn: () =>
            loadWalkIn(term, new Set(mineSignature.split(',')), DEFAULT_COLLECTIONS_DEPS),
        enabled: kind !== null,
        staleTime: 60_000,
        retry: false,
    });
    return {
        result: kind === null ? null : (query.data ?? null),
        isLoading: kind !== null && query.isPending,
    };
}

export type PaymentsRead =
    | { state: 'ready'; items: LoanPaymentListItem[] }
    | { state: 'loading' }
    | { state: 'forbidden' }
    | { state: 'error' };

/** Cuántos cobros enseña el historial del detalle. */
const LAST_PAYMENTS = 3;

/** Los últimos cobros del crédito walk-in, con el historial del vendedor (cadena). */
export function useLastPayments(creditId: string | null): PaymentsRead {
    const query = useQuery({
        queryKey: COLLECTIONS_KEYS.payments(creditId),
        queryFn: () => listSellerCreditPayments(creditId ?? '', { limit: LAST_PAYMENTS }),
        enabled: creditId !== null,
        staleTime: 60_000,
        retry: false,
    });
    if (query.data) return { state: 'ready', items: query.data.items.slice(0, LAST_PAYMENTS) };
    if (query.error) return { state: creditReadStateOf(query.error) };
    return { state: 'loading' };
}

/** Los cobros del crédito propio salen del MISMO request del detalle y siguen su estado. */
export function paymentsOfSellerLoad(load: SellerCreditLoad): PaymentsRead {
    const { read } = load;
    if (read.state === 'ready') return { state: 'ready', items: load.recentPayments };
    return read.state === 'loading' ? { state: 'loading' } : { state: read.state };
}
